import { useState } from 'react';
import { jobsInRange, periodRange, type Period } from '../../data/domain';
import { useJobs } from '../../data/hooks';
import { money, shortDate } from '../../lib/format';
import { printSection } from '../../lib/print';
import { navigate } from '../../lib/router';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, PanelHeader, View } from '../../ui/View';
import { Metric, PERIOD_OPTIONS } from './Dashboard';

export function TechnicianReportsView() {
  const jobs = useJobs();
  const initial = new URLSearchParams(window.location.search).get('period') as Period | null;
  const [period, setPeriod] = useState<Period>(initial && [...PERIOD_OPTIONS.map((option) => option.value), 'custom'].includes(initial) ? initial : 'fy');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const range = periodRange(period, { start, end });
  const inRange = jobsInRange(jobs, range);
  const technicians = [...new Set(inRange.map((job) => job.techName).filter(Boolean))].sort();
  const completed = inRange.filter((job) => job.completed && !job.cancelled).length;
  const planned = inRange.filter((job) => !job.cancelled && !job.completed).length;
  const cancelled = inRange.filter((job) => job.cancelled).length;
  const quoted = inRange.filter((job) => !job.cancelled).reduce((sum, job) => sum + (Number(job.quoteAmount) || 0), 0);
  const label = (date: Date) => shortDate(date.toISOString());

  return (
    <View id="technician-reports">
      <button className="back-link" id="backToDashboard" onClick={() => navigate('/')}><Icon name="arrowLeft" size={16} />Dashboard</button>
      <PageHeading eyebrow="Operations reporting" title="Technician reports" subtitle="Compare completed work, planned time, cancellations and quoted value by technician.">
        <button className="btn btn-secondary" id="printTechnicianReport" onClick={() => printSection('tech-report')}>Print report</button>
      </PageHeading>
      <div className="toolbar report-controls">
        <label>Time span
          <select id="reportPeriod" value={period} onChange={(event) => setPeriod(event.target.value as Period)}>
            {PERIOD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            <option value="custom">Custom dates</option>
          </select>
        </label>
        {period === 'custom' && <>
          <label>From <input type="date" value={start} onChange={(event) => setStart(event.target.value)} /></label>
          <label>To <input type="date" value={end} onChange={(event) => setEnd(event.target.value)} /></label>
        </>}
        <p className="hint toolbar-hint" id="reportRangeLabel">Reporting period: {label(range.start)} to {label(range.end)}</p>
      </div>
      <div className="stack">
        <section className="metrics" id="reportSummary" aria-label="Report summary">
          <Metric label="Completed jobs" value={completed} tone="success" />
          <Metric label="Planned jobs" value={planned} tone="warning" />
          <Metric label="Cancelled jobs" value={cancelled} />
          <Metric label="Quoted value" value={money(quoted)} />
        </section>
        <section className="panel technician-report-table">
          <PanelHeader title="Technician breakdown" summary="Work records within the selected reporting period" />
          <div className="row-list" id="technicianReportRows">
            {technicians.length ? technicians.map((tech) => {
              const own = inRange.filter((job) => job.techName === tech);
              const minutes = own.filter((job) => !job.cancelled).reduce((sum, job) => sum + (Number(job.duration) || 0), 0);
              const quote = own.filter((job) => !job.cancelled).reduce((sum, job) => sum + (Number(job.quoteAmount) || 0), 0);
              return (
                <div className="row report-row" key={tech}>
                  <span className="row-text"><b>{tech}</b><small>{own.length} total jobs</small></span>
                  <span className="figure"><b>{own.filter((job) => job.completed && !job.cancelled).length}</b><small>Completed</small></span>
                  <span className="figure"><b>{own.filter((job) => job.cancelled).length}</b><small>Cancelled</small></span>
                  <span className="figure"><b>{(minutes / 60).toFixed(1)} h</b><small>Planned time</small></span>
                  <span className="figure"><b>{money(quote)}</b><small>Quoted</small></span>
                </div>
              );
            }) : <EmptyState>No technician jobs in this reporting period.</EmptyState>}
          </div>
        </section>
      </div>
    </View>
  );
}
